// The one sale flow (operator, 2026-10-17): "there should always be some sort of
// sale before it can be applied to the service, whether that's through a package
// or an individually purchased service." /new-sale and the Pay dialog's "sell it
// now and use it" both render THIS form, so the two cannot drift.
//
// Two kinds of sale, each two separate acts the screen keeps visibly apart:
//   1. the ENTITLEMENT — a package (assignPackage) or treatments bought on their
//      own (sellServices); credits granted at once, spendable immediately;
//   2. the MONEY — recorded here (cash / EFT / gateway terminal), requested by
//      card link, or deliberately deferred ("collect later").
// A membership is the third way to pay for a package: a card billed every
// period, started by an emailed setup link, so it grants nothing today.
//
// Prices are the catalogue's — there is no price box. The one typed value is the
// amount on a part payment, prefilled to the full total, and the server refuses
// anything above the sale's balance.

import { useEffect, useMemo, useRef, useState } from 'react'
import { CreditCard, HandCoins, Hourglass, Minus, Plus, Repeat, X } from 'lucide-react'
import { Badge, Card, SearchInput, Tabs } from '@/components/data/ui'
import { Spinner } from '@/components/Spinner'
import { Labelled, NumberField, SelectField, TextField } from '@/components/editor/EditorKit'
import { CustomerField, type PickedCustomer } from '@/components/editor/CustomerField'
import { BackdateField } from '@/components/payments/BackdateField'
import { PAYMENT_METHODS, type PaymentMethodValue } from '@/lib/paymentMethods'
import {
  assignPackage,
  createMembership,
  createSalePaymentLink,
  listPackages,
  listServices,
  recordSalePayment,
  sellServices,
  PAGE_MAX,
  type PackageAssignment,
  type PackageDefinition,
  type ServiceRecord,
} from '@/lib/api'
import { readApiError } from '@/lib/apiError'
import { documentToastLine } from '@/lib/documents'
import { useApiResource } from '@/hooks/useApiData'
import { useRole } from '@/hooks/useRole'
import { fmtDate, fmtMoney } from '@/lib/format'
import { backdateField, backdateProblem } from '@/lib/backdating'
import { shiftLocalDate, todayInZone } from '@/lib/calendar'
import { cn } from '@/lib/utils'

export type SaleKind = 'package' | 'services'

/** How the money side of a sale is being handled. 'membership' bills a card every period instead. */
type PayRoute = 'record' | 'link' | 'later' | 'membership'

type Tone = 'good' | 'warn' | 'info'

/** What a completed sale tells the screen that hosts the form. */
export interface SoldSale {
  kind: 'package' | 'services' | 'membership'
  /** The sale — null for a membership, which grants nothing until its first payment. */
  sale: PackageAssignment | null
  customer: PickedCustomer
  heading: string
  lines: string[]
  money?: string
  moneyTone?: Tone
}

/** A treatment to start a services sale with — the Pay dialog's own session. */
export interface PresetService {
  id: string
  name: string
  price: number
}

export interface SaleFormProps {
  /** Fixed buyer (the Pay dialog). Absent = the operator picks one. */
  customer?: PickedCustomer
  startWith?: SaleKind
  presetService?: PresetService
  /** Offer only packages that could pay for this service — the dialog's "use it now". */
  coversServiceId?: string
  /** False where nothing can be applied today, e.g. selling to settle a session. */
  allowMembership?: boolean
  submitLabel?: string
  onSold: (sold: SoldSale) => void
}

export function SaleForm({
  customer: fixedCustomer,
  startWith = 'package',
  presetService,
  coversServiceId,
  allowMembership = true,
  submitLabel,
  onSold,
}: SaleFormProps) {
  const [kind, setKind] = useState<SaleKind>(startWith)
  // Held here so switching tabs keeps the buyer the operator already chose.
  const [picked, setPicked] = useState<PickedCustomer | null>(fixedCustomer ?? null)
  const customer = fixedCustomer ?? picked

  return (
    <div className="flex flex-col">
      <Tabs<SaleKind>
        tabs={[
          { key: 'package', label: allowMembership ? 'Package or membership' : 'Package' },
          { key: 'services', label: 'Services' },
        ]}
        value={kind}
        onChange={setKind}
      />
      <div className="flex flex-col gap-1 pt-3">
        {fixedCustomer ? null : (
          <Labelled label="Customer" required>
            <CustomerField value={picked} onChange={setPicked} />
          </Labelled>
        )}
        {kind === 'package' ? (
          <PackageSale
            customer={customer}
            coversServiceId={coversServiceId}
            allowMembership={allowMembership}
            submitLabel={submitLabel}
            onSold={onSold}
          />
        ) : (
          <ServicesSale customer={customer} presetService={presetService} submitLabel={submitLabel} onSold={onSold} />
        )}
      </div>
    </div>
  )
}

// ─── Paying for a sale ──────────────────────────────────────────────────────────

/** True unless the tenant config says card payments are off; absent reads as available (the API is the real gate). */
function useCardReady(): boolean {
  const { tenantConfig } = useRole()
  return (tenantConfig as { card_payments_configured?: boolean } | null)?.card_payments_configured !== false
}

function usePayment() {
  const { tenantConfig } = useRole()
  const today = todayInZone(tenantConfig?.timezone)
  // A sale entered late carries its real date; a card link or membership is dated when they pay.
  const [soldOn, setSoldOn] = useState(today)
  const [route, setRoute] = useState<PayRoute>('record')
  const [method, setMethod] = useState<PaymentMethodValue>('payment_gateway')
  const [amount, setAmount] = useState('')
  const [amountTouched, setAmountTouched] = useState(false)
  const [reference, setReference] = useState('')
  const dated = route === 'record' || route === 'later'
  return {
    route, setRoute, method, setMethod, reference, setReference, amountTouched,
    today, soldOn, setSoldOn, dated,
    /** The day the sale and its payment are recorded against. */
    saleDate: dated ? soldOn : today,
    amountText: (total: number) => (amountTouched ? amount : String(total)),
    setAmountText: (v: string) => { setAmount(v); setAmountTouched(true) },
    /** The full total until the operator types otherwise — paying in full is the common case. */
    effectiveAmount: (total: number) => (amountTouched && amount.trim() !== '' ? Number(amount) : total),
    /** A changed basket re-prefills to its new total. */
    resetAmount: () => { setAmount(''); setAmountTouched(false) },
  }
}
type Payment = ReturnType<typeof usePayment>

/** The money step's refusal, mirroring the server so the button is never one that can only fail. */
function paymentProblem(pay: Payment, total: number, currency: string, what: string): string | null {
  if (pay.dated) {
    const dateProblem = backdateProblem(pay.soldOn, pay.today)
    if (dateProblem) return dateProblem
  }
  if (pay.route !== 'record' || total <= 0) return null
  const amount = pay.effectiveAmount(total)
  if (!Number.isFinite(amount) || amount <= 0) return 'Enter an amount greater than zero.'
  if (amount > total) return `That is more than the ${fmtMoney(total, currency)} ${what}.`
  return null
}

/**
 * Take (or ask for) the money for a sale that now exists. The sale stands whatever
 * happens here, so a failure is reported as exactly that, never as no sale.
 */
async function takeMoney(sale: PackageAssignment, pay: Payment, total: number, currency: string): Promise<{ money: string; moneyTone: Tone }> {
  try {
    if (pay.route === 'record' && total <= 0) return { money: 'This sale is free — nothing to record.', moneyTone: 'good' }
    if (pay.route === 'record') {
      const amount = pay.effectiveAmount(total)
      const paid = await recordSalePayment(sale.id, {
        amount,
        payment_method: pay.method,
        reference: pay.reference.trim() || null,
        ...backdateField('paid_at', pay.saleDate, pay.today),
      })
      // The payment minted its own tax document server-side; say what it did.
      const docLine = documentToastLine(paid.document)
      return {
        money: (paid.payment_complete
          ? `${fmtMoney(amount, currency)} recorded — paid in full.`
          : `${fmtMoney(amount, currency)} recorded · ${fmtMoney(paid.balance_due, currency)} still owing.`)
          + (docLine ? ` ${docLine}` : ''),
        moneyTone: paid.payment_complete ? 'good' : 'warn',
      }
    }
    if (pay.route === 'link') {
      const link = await createSalePaymentLink(sale.id)
      const via = (link.sent_via ?? []).join(', ')
      return via
        ? { money: `Card payment link sent (${via}). The sale is unpaid until they pay it.`, moneyTone: 'info' }
        : { money: 'Card payment link created, but it could not be delivered on any channel — re-send it from Payment requests.', moneyTone: 'warn' }
    }
    return { money: 'No payment recorded yet — collect it later from Package sales.', moneyTone: 'warn' }
  } catch (e) {
    return {
      money: `The sale was recorded, but the payment step failed: ${readApiError(e)}. Record the money from Package sales when it is sorted.`,
      moneyTone: 'warn',
    }
  }
}

function PaymentChoice({
  pay,
  total,
  currency,
  saving,
  allowMembership,
}: {
  pay: Payment
  total: number
  currency: string
  saving: boolean
  allowMembership: boolean
}) {
  const cardReady = useCardReady()
  const methodHint = PAYMENT_METHODS.find((m) => m.value === pay.method)?.hint
  const noCard = 'Card payments are not set up for this business.'
  return (
    <>
      <Labelled label="Payment" required>
        <div className="flex flex-col gap-1.5">
          <PayRouteButton
            icon={HandCoins}
            active={pay.route === 'record'}
            disabled={saving}
            title="Paid now"
            detail="Cash, bank transfer, or the card terminal — record money already taken."
            onClick={() => pay.setRoute('record')}
          />
          <PayRouteButton
            icon={CreditCard}
            active={pay.route === 'link'}
            disabled={saving || !cardReady}
            title="Send a card payment link"
            detail={cardReady ? 'Emails and texts them a secure link for the full price. Unpaid until they pay it.' : noCard}
            onClick={() => pay.setRoute('link')}
          />
          <PayRouteButton
            icon={Hourglass}
            active={pay.route === 'later'}
            disabled={saving}
            title="Collect later"
            detail="Record the sale only. The credits are granted now; the money is handled separately."
            onClick={() => pay.setRoute('later')}
          />
          {allowMembership ? (
            <PayRouteButton
              icon={Repeat}
              active={pay.route === 'membership'}
              disabled={saving || !cardReady}
              title="Membership — bill every period"
              detail={cardReady
                ? 'Charges their card each period and grants a fresh set of credits each time. Starts with an emailed setup link.'
                : noCard}
              onClick={() => pay.setRoute('membership')}
            />
          ) : null}
        </div>
      </Labelled>

      {pay.dated ? (
        <BackdateField
          label="Date of sale"
          value={pay.soldOn}
          onChange={pay.setSoldOn}
          today={pay.today}
          disabled={saving}
          backdatedHint={(d) => `Recorded as sold on ${d}${pay.route === 'record' ? ', and the payment is dated the same' : ''}.`}
        />
      ) : null}

      {pay.route === 'record' ? (
        <>
          <Labelled label="How it was taken" required hint={methodHint}>
            <SelectField
              value={pay.method}
              onChange={pay.setMethod}
              disabled={saving}
              options={PAYMENT_METHODS.map((m) => ({ value: m.value, label: m.label }))}
            />
          </Labelled>
          <Labelled
            label="Amount"
            required
            hint={`Prefilled to the full ${fmtMoney(total, currency)}. Lower it only for a deposit or part payment.`}
          >
            <NumberField
              value={pay.amountText(total)}
              onChange={pay.setAmountText}
              min={0}
              step={1}
              prefix={currency}
              disabled={saving}
            />
          </Labelled>
          <Labelled label="Reference" hint="Receipt or transaction number from wherever it was taken. Optional.">
            <TextField value={pay.reference} onChange={pay.setReference} disabled={saving} />
          </Labelled>
        </>
      ) : null}

      {pay.route === 'membership' ? (
        <Card className="mb-2.5 bg-muted/40">
          <p className="text-[12.5px] text-muted-foreground">
            The customer is emailed a secure link to put a card on file. Nothing is active until that
            first payment lands; each period then charges automatically and grants a fresh set of credits.
          </p>
        </Card>
      ) : null}
    </>
  )
}

// ─── Selling a package (or starting a membership on one) ────────────────────────

function packageLabel(p: PackageDefinition, currency: string): string {
  const sessions = p.sessions_total > 0
    ? `${p.sessions_total} session${p.sessions_total === 1 ? '' : 's'}`
    : 'plan'
  return `${p.name} · ${sessions} · ${fmtMoney(p.price, p.currency ?? currency)}`
}

/** Could this definition pay for that service? Empty service_ids covers everything (the category gate is the server's call). */
function covers(p: PackageDefinition, serviceId: string): boolean {
  return (p.service_ids ?? []).length === 0 || (p.service_ids ?? []).includes(serviceId)
}

function PackageSale({
  customer,
  coversServiceId,
  allowMembership,
  submitLabel,
  onSold,
}: {
  customer: PickedCustomer | null
  coversServiceId?: string
  allowMembership: boolean
  submitLabel?: string
  onSold: (sold: SoldSale) => void
}) {
  const { tenantConfig } = useRole()
  const currency = tenantConfig?.currency ?? 'AUD'
  // The definitions on sale fit one page (the first tenant has 20), searched client-side.
  const packages = useApiResource(() => listPackages({ limit: PAGE_MAX }), [])
  const [search, setSearch] = useState('')
  const [packageId, setPackageId] = useState('')
  const pay = usePayment()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const items = useMemo(() => {
    const all = packages.data?.items ?? []
    return coversServiceId ? all.filter((p) => covers(p, coversServiceId)) : all
  }, [packages.data, coversServiceId])
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? items.filter((p) => p.name.toLowerCase().includes(q)) : items
  }, [items, search])

  const chosen = items.find((p) => p.id === packageId) ?? null
  const price = chosen ? Number(chosen.price) : 0
  const membership = pay.route === 'membership'

  const blocker = !customer
    ? 'Choose who is buying.'
    : !chosen
      ? 'Choose what they are buying.'
      : membership ? null : paymentProblem(pay, price, currency, 'this package costs')

  const sell = async () => {
    if (blocker || !customer || !chosen) { setError(blocker); return }
    setSaving(true)
    setError(null)

    if (membership) {
      try {
        await createMembership({ customer_id: customer.id, package_id: chosen.id })
        onSold({
          kind: 'membership',
          sale: null,
          customer,
          heading: 'Setup link sent',
          lines: [
            `${customer.name} has been emailed a secure link to put a card on file for ${chosen.name}.`,
            'The membership stays pending until they complete that first payment — nothing is active yet. Each period then charges automatically and grants a fresh set of credits.',
          ],
        })
      } catch (e) {
        setError(readApiError(e))
        setSaving(false)
      }
      return
    }

    // The entitlement first — it IS the sale. If the money step then fails the
    // sale still stands and the result says exactly what to do next.
    let sale: PackageAssignment
    try {
      sale = await assignPackage(customer.id, { package_id: chosen.id, ...backdateField('purchased_at', pay.saleDate, pay.today) })
    } catch (e) {
      setError(readApiError(e))
      setSaving(false)
      return
    }
    const money = await takeMoney(sale, pay, price, currency)
    onSold({
      kind: 'package',
      sale,
      customer,
      heading: `${chosen.name} sold`,
      lines: [
        `${customer.name} now holds ${chosen.sessions_total > 0
          ? `${chosen.sessions_total} session credit${chosen.sessions_total === 1 ? '' : 's'}`
          : 'this plan'}${chosen.validity_days ? `, valid ${chosen.validity_days} days` : ''}.`,
      ],
      ...money,
    })
  }

  return (
    <>
      <Labelled
        label="Package"
        required
        hint={packages.error ? undefined : coversServiceId
          ? 'Only packages on sale that cover this session are offered.'
          : 'Only packages currently on sale are offered.'}
      >
        {packages.loading ? (
          <div className="py-2"><Spinner size={16} /></div>
        ) : packages.error ? (
          <p className="text-[12.5px] text-rose-700">{packages.error}</p>
        ) : (
          <>
            {items.length > 8 ? (
              <div className="mb-1.5">
                <SearchInput value={search} onChange={setSearch} placeholder="Filter packages…" />
              </div>
            ) : null}
            <SelectField
              value={packageId}
              onChange={(id) => { setPackageId(id); pay.resetAmount() }}
              disabled={saving}
              options={[
                {
                  value: '',
                  label: filtered.length === 0
                    ? (items.length === 0 ? 'No packages on sale' : 'Nothing matches that filter')
                    : `Choose a package… (${filtered.length})`,
                },
                ...filtered.map((p) => ({ value: p.id, label: packageLabel(p, currency) })),
              ]}
            />
          </>
        )}
      </Labelled>

      {chosen ? (
        <Card className="mb-2.5 bg-muted/40">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[13px] font-semibold text-foreground">{chosen.name}</span>
            <Badge label={fmtMoney(price, chosen.currency ?? currency)} tone="info" />
          </div>
          <p className="mt-1 text-[12px] text-muted-foreground">
            {chosen.sessions_total > 0
              ? `Grants ${chosen.sessions_total} session credit${chosen.sessions_total === 1 ? '' : 's'}, spendable immediately. `
              : ''}
            {chosen.validity_days
              ? pay.saleDate === pay.today
                ? `Credits expire ${chosen.validity_days} days from today.`
                : `Credits expire ${chosen.validity_days} days from the sale date, on ${fmtDate(shiftLocalDate(pay.saleDate, chosen.validity_days))}.`
              : 'These credits never expire.'}
          </p>
          {(chosen.services ?? []).some((s) => s.sessions != null) ? (
            <p className="mt-1 text-[12px] text-muted-foreground">
              {(chosen.services ?? [])
                .map((s) => `${s.sessions ?? 1}× ${s.name ?? `service (${s.id.slice(0, 8)})`}`)
                .join(' · ')}
              {' — each service tracked separately.'}
            </p>
          ) : null}
        </Card>
      ) : null}

      {chosen && price > 0 ? (
        <PaymentChoice pay={pay} total={price} currency={currency} saving={saving} allowMembership={allowMembership} />
      ) : null}

      <SubmitRow
        error={error ?? blocker}
        saving={saving}
        disabled={Boolean(blocker)}
        label={membership ? 'Email the setup link' : (submitLabel ?? 'Complete sale')}
        savingLabel={membership ? 'Sending…' : 'Recording…'}
        onClick={() => void sell()}
      />
    </>
  )
}

// ─── Selling services on their own ──────────────────────────────────────────────

/** `price` is the list price; `priceText` is the agreed price as typed, which is a discount when lower. */
interface Line extends PresetService { quantity: number; priceText: string }

const priceInput = (n: number) => n.toFixed(2)

/** The agreed price per session as typed, or null while it is not a valid amount. */
function agreedPrice(l: Line): number | null {
  const t = l.priceText.trim().replace(/^\$/, '').replace(/,/g, '')
  return /^\d+(\.\d{0,2})?$/.test(t) ? Number(t) : null
}

const sameCents = (a: number, b: number) => Math.round(a * 100) === Math.round(b * 100)

/** The catalogue, searched by the SERVER — a screen only ever holds a page, so filtering it locally would lie past row 100. */
function useServiceSearch(term: string) {
  const [state, setState] = useState<{ loading: boolean; error: string | null; items: ServiceRecord[] }>({
    loading: true, error: null, items: [],
  })
  const seq = useRef(0)
  useEffect(() => {
    const mine = ++seq.current
    const timer = setTimeout(() => {
      listServices(false, { search: term.trim() || undefined, limit: PAGE_MAX })
        .then((page) => { if (mine === seq.current) setState({ loading: false, error: null, items: page.items }) })
        .catch((e) => { if (mine === seq.current) setState({ loading: false, error: readApiError(e), items: [] }) })
    }, term ? 250 : 0)
    return () => clearTimeout(timer)
  }, [term])
  return state
}

function ServicesSale({
  customer,
  presetService,
  submitLabel,
  onSold,
}: {
  customer: PickedCustomer | null
  presetService?: PresetService
  submitLabel?: string
  onSold: (sold: SoldSale) => void
}) {
  const { tenantConfig } = useRole()
  const currency = tenantConfig?.currency ?? 'AUD'
  const [term, setTerm] = useState('')
  const search = useServiceSearch(term)
  const [lines, setLines] = useState<Line[]>(presetService
    ? [{ ...presetService, quantity: 1, priceText: priceInput(presetService.price) }]
    : [])
  const pay = usePayment()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The server prices the sale: each line at its agreed price, else the catalogue's.
  const unitOf = (l: Line) => agreedPrice(l) ?? l.price
  const total = Math.round(lines.reduce((sum, l) => sum + unitOf(l) * l.quantity, 0) * 100) / 100
  const listTotal = Math.round(lines.reduce((sum, l) => sum + l.price * l.quantity, 0) * 100) / 100
  const sessions = lines.reduce((sum, l) => sum + l.quantity, 0)
  const badPrice = lines.find((l) => agreedPrice(l) === null)

  const add = (svc: { id: string; name: string; price: number }) => {
    pay.resetAmount()
    setLines((prev) => prev.some((l) => l.id === svc.id)
      ? prev.map((l) => (l.id === svc.id ? { ...l, quantity: l.quantity + 1 } : l))
      : [...prev, { id: svc.id, name: svc.name, price: Number(svc.price) || 0, quantity: 1, priceText: priceInput(Number(svc.price) || 0) }])
  }
  const bump = (id: string, by: number) => {
    pay.resetAmount()
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, quantity: Math.max(1, l.quantity + by) } : l)))
  }
  const remove = (id: string) => {
    pay.resetAmount()
    setLines((prev) => prev.filter((l) => l.id !== id))
  }
  // Changing what a session is worth is a discount; the amount paid today follows it.
  const setPrice = (id: string, text: string) => {
    pay.resetAmount()
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, priceText: text } : l)))
  }

  const blocker = !customer
    ? 'Choose who is buying.'
    : lines.length === 0
      ? 'Add at least one service.'
      : badPrice
        ? `Enter a price for ${badPrice.name}, e.g. ${priceInput(badPrice.price)}.`
        : paymentProblem(pay, total, currency, 'these services cost')

  const sell = async () => {
    if (blocker || !customer) { setError(blocker); return }
    setSaving(true)
    setError(null)
    let sale: PackageAssignment
    try {
      sale = await sellServices(customer.id, {
        // A price goes only with a line the operator changed; the rest are the catalogue's.
        services: lines.map((l) => {
          const unit = agreedPrice(l) ?? l.price
          return sameCents(unit, l.price)
            ? { service_id: l.id, quantity: l.quantity }
            : { service_id: l.id, quantity: l.quantity, unit_price: unit }
        }),
        ...backdateField('purchased_at', pay.saleDate, pay.today),
      })
    } catch (e) {
      setError(readApiError(e))
      setSaving(false)
      return
    }
    // The server's price is the sale's; the money step is judged against it.
    const money = await takeMoney(sale, pay, Number(sale.price ?? total), currency)
    onSold({
      kind: 'services',
      sale,
      customer,
      heading: 'Services sold',
      lines: [
        `${customer.name} now holds ${lines.map((l) => `${l.quantity} × ${l.name}`).join(', ')}.`,
        'Used one session per booking. These never expire.',
      ],
      ...money,
    })
  }

  return (
    <>
      <Labelled label="Services" required hint="Each one becomes sessions on their account, used one per booking. They never expire. Change a price to give a discount; paying less than the total is a part payment.">
        <div className="flex flex-col gap-1.5">
          {lines.length > 0 ? (
            <ul className="flex flex-col divide-y divide-border rounded-lg border border-border bg-card">
              {lines.map((l) => (
                <li key={l.id} className="flex items-center gap-2 px-2.5 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-foreground">{l.name}</span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11.5px] text-muted-foreground">
                      <span
                        className={cn(
                          'flex h-7 items-center rounded-md border bg-background px-1.5 focus-within:ring-[3px] focus-within:ring-ring/50',
                          agreedPrice(l) === null ? 'border-rose-400' : 'border-input',
                        )}
                      >
                        <span aria-hidden>$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Price each for ${l.name}`}
                          value={l.priceText}
                          disabled={saving}
                          onChange={(e) => setPrice(l.id, e.target.value)}
                          className="w-16 bg-transparent text-[12.5px] text-foreground tabular-nums outline-none"
                        />
                      </span>
                      <span>each · {fmtMoney(unitOf(l) * l.quantity, currency)}</span>
                      {!sameCents(unitOf(l), l.price) ? <span>· list {fmtMoney(l.price, currency)}</span> : null}
                    </span>
                  </span>
                  <span className="flex items-center gap-1">
                    <IconButton label={`One fewer ${l.name}`} disabled={saving || l.quantity <= 1} onClick={() => bump(l.id, -1)}>
                      <Minus size={13} aria-hidden />
                    </IconButton>
                    <span className="w-6 text-center text-[13px] font-semibold tabular-nums" aria-label={`${l.quantity} sessions`}>
                      {l.quantity}
                    </span>
                    <IconButton label={`One more ${l.name}`} disabled={saving} onClick={() => bump(l.id, 1)}>
                      <Plus size={13} aria-hidden />
                    </IconButton>
                    <IconButton label={`Remove ${l.name}`} disabled={saving} onClick={() => remove(l.id)}>
                      <X size={13} aria-hidden />
                    </IconButton>
                  </span>
                </li>
              ))}
              <li className="flex items-center justify-between px-2.5 py-2 text-[13px]">
                <span className="text-muted-foreground">{sessions} session{sessions === 1 ? '' : 's'}</span>
                <span className="text-right">
                  <span className="block font-semibold text-foreground">{fmtMoney(total, currency)}</span>
                  {!sameCents(total, listTotal) ? (
                    <span className="block text-[11.5px] text-muted-foreground">
                      {total < listTotal
                        ? `${fmtMoney(listTotal - total, currency)} off list prices`
                        : `${fmtMoney(total - listTotal, currency)} above list prices`}
                    </span>
                  ) : null}
                </span>
              </li>
            </ul>
          ) : null}

          <SearchInput value={term} onChange={setTerm} placeholder="Find a service to add…" />
          {search.loading ? (
            <div className="py-1"><Spinner size={16} /></div>
          ) : search.error ? (
            <p className="text-[12.5px] text-rose-700">{search.error}</p>
          ) : search.items.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">{term ? 'No service matches that.' : 'No services on sale.'}</p>
          ) : (
            <ul className="max-h-52 overflow-y-auto rounded-lg border border-border bg-card" aria-label="Services to add">
              {search.items.map((svc) => (
                <li key={svc.id}>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => add(svc)}
                    className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12.5px] hover:bg-muted disabled:opacity-40"
                  >
                    <Plus size={13} className="shrink-0 text-muted-foreground" aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-foreground">{svc.name}</span>
                    <span className="shrink-0 text-muted-foreground">{fmtMoney(Number(svc.price) || 0, svc.currency ?? currency)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Labelled>

      {lines.length > 0 && total > 0 ? (
        <PaymentChoice pay={pay} total={total} currency={currency} saving={saving} allowMembership={false} />
      ) : null}

      <SubmitRow
        error={error ?? blocker}
        saving={saving}
        disabled={Boolean(blocker)}
        label={submitLabel ?? 'Complete sale'}
        savingLabel="Recording…"
        onClick={() => void sell()}
      />
    </>
  )
}

// ─── Shared bits ────────────────────────────────────────────────────────────────

function SubmitRow({
  error,
  saving,
  disabled,
  label,
  savingLabel,
  onClick,
}: {
  error: string | null
  saving: boolean
  disabled: boolean
  label: string
  savingLabel: string
  onClick: () => void
}) {
  return (
    <>
      {error ? <p className="text-[12.5px] text-rose-700">{error}</p> : null}
      <div className="mt-1.5 flex justify-end">
        <button
          type="button"
          onClick={onClick}
          disabled={saving || disabled}
          className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-4 py-2 text-[13px] font-semibold text-background hover:opacity-90 disabled:opacity-40"
        >
          {saving ? savingLabel : label}
        </button>
      </div>
    </>
  )
}

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex size-7 items-center justify-center rounded-md border border-border bg-card text-foreground hover:bg-muted disabled:opacity-40"
    >
      {children}
    </button>
  )
}

function PayRouteButton({
  icon: Icon,
  active,
  disabled,
  title,
  detail,
  onClick,
}: {
  icon: typeof HandCoins
  active: boolean
  disabled: boolean
  title: string
  detail: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`flex w-full items-start gap-2.5 rounded-lg border p-2.5 text-left transition-colors disabled:opacity-40 ${
        active ? 'border-foreground bg-muted/60' : 'border-border bg-card hover:bg-muted/40'
      }`}
    >
      <Icon size={17} className={`mt-0.5 shrink-0 ${active ? 'text-foreground' : 'text-muted-foreground'}`} aria-hidden />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-foreground">{title}</span>
        <span className="mt-px block text-[12px] text-muted-foreground">{detail}</span>
      </span>
    </button>
  )
}
